import type {
  ConsignmentStatementLine,
  ConsignmentBankReceiptReview,
} from './consignment-statements';

export const CONSIGNMENT_PAYMENT_METHODS = [
  'BANK_TRANSFER',
  'CHECK',
  'PAYPAL',
  'CASH',
  'OTHER',
] as const;
export type ConsignmentPaymentMethod = (typeof CONSIGNMENT_PAYMENT_METHODS)[number];
export interface ConsignmentFinanceSummary {
  version: number;
  adjustmentAmountMinor: number;
  paidAmountMinor: number;
  balanceAmountMinor: number;
}
export interface ConsignmentFinanceEvent {
  requestId: string;
  version: number;
  action: 'PAYMENT_RECORDED' | 'PAYMENT_VOIDED' | 'ADJUSTMENT_APPROVED';
  amountMinor: number;
  at: string;
  actor: string;
  reason?: string;
  paymentId?: string;
  paymentDate?: string;
  method?: ConsignmentPaymentMethod;
  reference?: string;
  bankReceiptReview?: ConsignmentBankReceiptReview;
}
export interface ConsignmentFinanceDetail {
  summary: ConsignmentFinanceSummary;
  events: ConsignmentFinanceEvent[];
  cursor?: string;
  preview: {
    fingerprint: string;
    needsReview: boolean;
    adjustmentAmountMinor: number;
    lines: ConsignmentStatementLine[];
    feeCharges?: import('./consignment-charges').ConsignmentFeeChargeLine[];
    blockers: string[];
  };
}
type FinanceRequest = {
  consignorId: string;
  statementId: string;
  requestId: string;
  version: number;
};
export type ConsignmentFinanceMutation = FinanceRequest &
  (
    | {
        operation: 'RECORD_STATEMENT_PAYMENT';
        amountMinor: number;
        paymentDate: string;
        method: ConsignmentPaymentMethod;
        reference: string;
        transferred: boolean;
        bankReceiptReviewed?: boolean;
        bankReceiptReference?: string;
      }
    | { operation: 'VOID_STATEMENT_PAYMENT'; paymentId: string; reason: string }
    | {
        operation: 'APPROVE_STATEMENT_ADJUSTMENT';
        fingerprint: string;
        reason: string;
        reviewed: boolean;
      }
  );
export function consignmentPaymentStatus(summary?: ConsignmentFinanceSummary): string {
  if (!summary) return 'Approved (unpaid)';
  if (summary.balanceAmountMinor < 0) return 'Recovery due';
  if (summary.balanceAmountMinor === 0)
    return summary.paidAmountMinor > 0 ? 'Paid (recorded)' : 'No payment due';
  return summary.paidAmountMinor > 0 ? 'Partially paid' : 'Approved (unpaid)';
}
